/**
 * @fileoverview Vuex store watchers for reactive search indexing.
 *
 * Sets up Fuse.js search indices that automatically update when store
 * data changes, enabling fast fuzzy search across various data types.
 *
 * @module helpers/storeWatcher
 */

import Konami from "@/konami";
import Fuse from "fuse.js";
import _ from "lodash";

/**
 * Initializes Vuex store watchers for reactive search functionality.
 *
 * Creates watchers that automatically rebuild Fuse.js search indices when
 * the corresponding store data changes. Also initializes the Konami code
 * easter egg handler.
 *
 * Watched state paths and their search indices:
 * - `nodes.event` -> `search.event` (events + $display, $zopim)
 * - `apieditor.mappings` -> `search.api_mappings` (API mapping keys)
 * - `nodes.content` -> `search.content` (content node IDs, base nodes only)
 * - `nodes.entity` -> `search.entity` (entity keys)
 * - `forms` -> `search.form` (form names)
 *
 * @param {Object} store - The Vuex store instance
 * @returns {void}
 *
 * @example
 * // Called during app bootstrap
 * initStoreWatcher(store);
 *
 * // Later, use search indices in components
 * const results = this.$store.state.search.content.search('hello');
 */
export function initStoreWatcher(store: any) {
  new (Konami as any)(() => {
    store.dispatch("KONAMI");
  });

  store.watch(
    (state: any) => state.nodes.event,
    (event: any) => {
      const searchList = ["$display", "$zopim", ...event].map((key) => ({ value: key })) || [];
      store.state.search.event = new Fuse(searchList, {
        keys: ["value"],
      });
    }
  );

  store.watch(
    (state: any) => state.apieditor.mappings,
    (mappings: any) => {
      const searchList = _.keys(mappings).map((key: any) => ({ value: key })) || [];
      store.state.search.api_mappings = new Fuse(searchList, {
        keys: ["value"],
      });
    }
  );

  store.watch(
    (state: any) => state.nodes.content,
    (content: any) => {
      const contentNodeIds = _.keys(content);
      const baseContentNodeIds = _.filter(contentNodeIds, (key: any) => !key.includes(":"));
      const mappedBaseContentNodeIds = _.map(baseContentNodeIds, (key: any) => ({
        value: key,
      }));
      store.state.search.content = new Fuse(mappedBaseContentNodeIds || [], {
        keys: ["value"],
      });
    }
  );

  store.watch(
    (state: any) => _.get(state, "nodes.entity", {}),
    (content: any) => {
      const searchList = _.keys(content).map((key: any) => ({ value: key })) || [];
      store.state.search.entity = new Fuse(searchList, {
        keys: ["value"],
      });
    }
  );

  store.watch(
    (state: any) => _.get(state, "forms", {}),
    (forms: any) => {
      const searchList = _.map(forms, (form: any) => ({ value: form.name })) || [];
      store.state.search.form = new Fuse(searchList, {
        keys: ["value"],
      });
    }
  );
}
